import React, { useState } from 'react';
import AdComponent from '../components/AdComponent';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaYoutube, FaInstagram, FaPaperPlane, FaWhatsapp } from 'react-icons/fa';

const Contact = ({ YOUTUBE_CHANNEL, INSTAGRAM_PROFILE }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    goal: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Try Resend backend first (for beautiful emails)
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        setSubmitStatus({ 
          type: 'success', 
          message: 'Thank you! Your message has been sent successfully. I will get back to you soon!' 
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          goal: '',
          message: ''
        });
        return;
      }
    } catch (error) {
      console.log('Resend backend not available, trying Web3Forms...');
    }

    try {
      // Fallback to Web3Forms
      const formDataToSend = new FormData();
      formDataToSend.append('access_key', process.env.REACT_APP_WEB3FORMS_KEY || 'YOUR_WEB3FORMS_ACCESS_KEY');
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone || 'Not provided');
      formDataToSend.append('subject', `New Contact from ${formData.name} - Fitness Goal: ${formData.goal || 'General'}`);
      formDataToSend.append('message', `
Fitness Goal: ${formData.goal || 'Not specified'}

Message:
${formData.message}
      `);

      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formDataToSend
      });

      const data = await response.json();

      if (data.success) {
        setSubmitStatus({ 
          type: 'success', 
          message: 'Thank you! Your message has been sent successfully. I will get back to you soon!' 
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          goal: '',
          message: ''
        });
        return;
      }
    } catch (error) {
      console.log('Web3Forms failed, using mailto fallback...');
    }

    // Final fallback to mailto
    try {
      const subject = `New Contact: ${formData.name} - ${formData.goal || 'General Inquiry'}`;
      const body = `
Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone || 'Not provided'}
Fitness Goal: ${formData.goal || 'Not specified'}

Message:
${formData.message}

---
Sent from Coach Sabari Fitness Website
      `;
      
      const mailtoLink = `mailto:sabariprimefit@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.open(mailtoLink, '_blank');
      
      setSubmitStatus({ 
        type: 'success', 
        message: 'Opening your email client... Please send the email to complete your message.' 
      });
      
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          phone: '',
          goal: '',
          message: ''
        });
      }, 2000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus({ 
        type: 'error', 
        message: 'Failed to send message. Please try WhatsApp or email directly: sabariprimefit@gmail.com' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-yellow-400 uppercase tracking-wide mb-3 sm:mb-4">
            Get In Touch
          </h1>
          <p className="text-gray-400 text-base sm:text-lg">
            Ready to start your fitness journey? Let's talk!
          </p>
        </div>

        {/* AdSense Ad Unit */}
        <div className="mb-8 flex justify-center">
          <AdComponent />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Contact Info */}
          <div className="space-y-6">
            {/* Contact Cards */}
            <div className="bg-gray-900 border-2 border-yellow-500/50 rounded-xl p-6">
              <div className="bg-gradient-to-r from-yellow-500 to-orange-600 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                <FaEnvelope className="text-black text-xl" />
              </div>
              <h3 className="text-lg font-bold text-yellow-400 uppercase mb-2">Email</h3>
              <p className="text-gray-400 text-sm">sabariprimefit@gmail.com</p>
            </div>

            <div className="bg-gray-900 border-2 border-orange-500/50 rounded-xl p-6">
              <div className="bg-gradient-to-r from-orange-500 to-red-600 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                <FaPhone className="text-black text-xl" />
              </div>
              <h3 className="text-lg font-bold text-orange-400 uppercase mb-2">Phone</h3>
              <p className="text-gray-400 text-sm">+91 6383767287</p>
            </div>

            <div className="bg-gray-900 border-2 border-red-500/50 rounded-xl p-6">
              <div className="bg-gradient-to-r from-red-500 to-red-700 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                <FaMapMarkerAlt className="text-white text-xl" />
              </div>
              <h3 className="text-lg font-bold text-red-400 uppercase mb-2">Location</h3>
              <p className="text-gray-400 text-sm">Tamil Nadu, India</p>
            </div>

            {/* Social Links */}
            <div className="bg-gradient-to-r from-yellow-500 via-orange-500 to-red-600 rounded-xl p-1">
              <div className="bg-black rounded-xl p-6">
                <h3 className="text-lg font-bold text-yellow-400 uppercase mb-4">Follow Me</h3>
                <div className="flex space-x-4">
                  <a
                    href={YOUTUBE_CHANNEL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-red-600 hover:bg-red-700 text-white p-4 rounded-lg transition-all transform hover:scale-110 flex-1 flex items-center justify-center"
                  >
                    <FaYoutube size={24} />
                  </a>
                  <a
                    href={INSTAGRAM_PROFILE}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white p-4 rounded-lg transition-all transform hover:scale-110 flex-1 flex items-center justify-center"
                  >
                    <FaInstagram size={24} />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-gradient-to-r from-yellow-500 via-orange-500 to-red-600 rounded-2xl p-1">
              <div className="bg-black rounded-2xl p-6 sm:p-8">
                <h2 className="text-xl sm:text-2xl font-bold text-yellow-400 uppercase tracking-wide mb-4 sm:mb-6">
                  Send Me a Message
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {submitStatus && (
                    <div className={`p-4 rounded-lg ${
                      submitStatus.type === 'success' 
                        ? 'bg-green-900/50 border-2 border-green-500 text-green-300' 
                        : 'bg-red-900/50 border-2 border-red-500 text-red-300'
                    }`}>
                      {submitStatus.message}
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-gray-400 font-semibold mb-2 text-sm uppercase">
                        Your Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Enter Your Name"
                        className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-400 transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-gray-400 font-semibold mb-2 text-sm uppercase">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="Enter Your Email"
                        className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-lg text-white focus:outline-none focus:border-orange-400 transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-gray-400 font-semibold mb-2 text-sm uppercase">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Enter Your Phone Number"
                        className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-lg text-white focus:outline-none focus:border-red-400 transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-gray-400 font-semibold mb-2 text-sm uppercase">
                        Fitness Goal
                      </label>
                      <select
                        name="goal"
                        value={formData.goal}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-400 transition-all"
                      >
                        <option value="">Select a goal</option>
                        <option value="weight-loss">Weight Loss</option>
                        <option value="muscle-gain">Muscle Gain</option>
                        <option value="strength">Strength Training</option>
                        <option value="general">General Fitness</option>
                        <option value="nutrition">Nutrition Coaching</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-400 font-semibold mb-2 text-sm uppercase">
                      Your Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="6"
                      placeholder="Tell me about your fitness goals and how I can help you..."
                      className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-lg text-white focus:outline-none focus:border-orange-400 transition-all resize-none"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full bg-gradient-to-r from-yellow-500 via-orange-500 to-red-600 hover:from-yellow-600 hover:via-orange-600 hover:to-red-700 text-black font-bold py-4 rounded-lg uppercase tracking-wide transition-all transform hover:scale-105 flex items-center justify-center space-x-2 ${
                      isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <FaPaperPlane />
                        <span>Send Message</span>
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>

            {/* Info Box */}
            <div className="mt-6 bg-gray-900 border-2 border-yellow-500/30 rounded-xl p-6">
              <h3 className="text-lg font-bold text-yellow-400 uppercase mb-3">
                💪 Ready to Transform?
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-4">
                Whether you're looking to lose weight, build muscle, or improve your overall fitness, 
                I'm here to help you achieve your goals. Fill out the form above and I'll get back to 
                you within 24 hours to discuss your personalized training program.
              </p>
              <a
                href="https://chat.whatsapp.com/CAsrkjCEANc4f9unrUxkn0"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-bold transition-all transform hover:scale-105 w-full justify-center"
              >
                <FaWhatsapp size={20} />
                <span>Join WhatsApp Community</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
